/**
 * Resolves the logged-in partner from `/api/portal/me`. The result has three
 * states:
 *
 *   loading   — initial fetch in flight
 *   ok        — profile loaded; portal renders
 *   forbidden — auth succeeded at the JWT layer but the user's email isn't a
 *               registered active partner (admin email, suspended, pending)
 *   error     — anything else (network, 5xx)
 *
 * Components use this to gate the portal Shell. The /me endpoint is also the
 * canary: if the JWT or dev-key is wrong/expired, AuthProvider catches that
 * before we get here, so a non-200 from /me always means a *registration*
 * problem, not an *auth* problem.
 */

import { useEffect, useState } from "react";

import { api, type ApiError } from "../../../lib/api";
import type { PartnerProfile } from "../types";

export type PartnerProfileState =
  | { status: "loading" }
  | { status: "ok"; profile: PartnerProfile }
  | { status: "forbidden"; reason: string }
  | { status: "error"; message: string };

export function usePartnerProfile(): PartnerProfileState & { refetch: () => void } {
  const [state, setState] = useState<PartnerProfileState>({ status: "loading" });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    api
      .get<PartnerProfile>("/portal/me")
      .then((profile) => {
        if (!cancelled) setState({ status: "ok", profile });
      })
      .catch((err: ApiError) => {
        if (cancelled) return;
        if (err.status === 403) {
          setState({ status: "forbidden", reason: err.message || "not_a_registered_partner" });
        } else {
          setState({ status: "error", message: err.message || "Unknown error" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { ...state, refetch: () => setTick((t) => t + 1) };
}
